import React, { useEffect, useRef, useState, useMemo } from 'react';
import { ReactComponent as CloseIcon } from '../../../images/close-x-icon.svg';
import { XYChart } from '@amcharts/amcharts5/xy';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import { handleChartDownloadUtil } from '../../../utilities/ExportChart';
import Loader from '../../../features/common/Loader/Loader';
import FullScreenIcon from '../../../features/common/FullScreenIcon/FullScreenIcon';
import DownloadChart from '../download/downloadChart';
import EmptyState from '../../empty-state/EmptyState';
import { createTimeSeriesChart } from '../../../utilities/COVChartUtils';
import { ChartConfig } from '../../../utilities/chartUtils';
import {
  getSortedChartData,
  syncCursor as syncCursorUtil,
  syncAxes as syncAxesUtil,
} from '../../../utilities/chartHelpers';
import { ProtectAction } from '../../../rbac';
import NoDataComponent from '../../../features/common/NoDataComponent/NoDataComponent';
import { calculateAndSetChartDataCounts } from '../../../well-chart/WidgetLayoutSlice';
import Helpers from '../../../utilities/Helpers';

interface TimeSeriesDataProps {
  timeseriesChartData: any;
  rPermission: { [key: string]: boolean };
  externalChartRefs?: React.MutableRefObject<(XYChart | null)[]>;
  onChartRegistered?: (index: number, chart: any) => void;
}

const COVTimeSeriesChart: React.FC<TimeSeriesDataProps> = ({
  timeseriesChartData,
  rPermission,
  externalChartRefs,
  onChartRegistered,
}) => {
  const dispatch = useAppDispatch();
  const chartRefs = useRef<(HTMLDivElement | null)[]>([]);
  const amCharRefs = useRef<XYChart[] | null[]>([null, null, null, null]);
  const fullscreenTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [chartRoots, setChartRoots] = useState<any[]>([]);
  const [fullscreenChartIndex, setFullscreenChartIndex] = useState<number | null>(null);
  const chartData = useAppSelector((state: any) => state.AssetTrendDataSlice.data);
  const chartDataLoading = useAppSelector((state: any) => state.AssetTrendDataSlice.loading);
  const chartViewOptions = useAppSelector((state: any) => state.AssetTrendDataSlice.chartViewOptions);
  const parameterData = timeseriesChartData?.widgetProperty.widgetProperties.parameterConfig[0]?.parameters;
  const sortedData = useMemo(() => {
    const data = getSortedChartData(chartData, parameterData);
    return data.map((chart: any) => ({
      ...chart,
      originalTitle: chart.title,
      title: Helpers.toSentenceCase(chart.title),
    }));
  }, [chartData, parameterData]);
  const startDate = useAppSelector((state: any) => state.AssetTrendDataSlice.startDate);
  const endDate = useAppSelector((state: any) => state.AssetTrendDataSlice.endDate);

  const hasPlottableData = sortedData.some((d: any) => Array.isArray(d.dataPoints) && d.dataPoints.length > 0);

  // Cleanup: Dispose all resources when component unmounts
  useEffect(() => {
    return () => {
      if (fullscreenTimeoutRef.current) {
        clearTimeout(fullscreenTimeoutRef.current);
      }

      chartRoots.forEach((root) => {
        if (root && typeof root.dispose === 'function') {
          try {
            root.dispose();
          } catch (e) {
            console.error('Error disposing chart root on unmount:', e);
          }
        }
      });

      amCharRefs.current = [null, null, null, null];

      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.error(`Error exiting fullscreen on unmount: ${err.message}`);
        });
      }
    };
  }, []);

  useEffect(() => {
    dispatch(calculateAndSetChartDataCounts(sortedData ?? []));
  }, [sortedData, dispatch]);

  const addAmChartRef = (index: number, chart: XYChart) => {
    if (chart) {
      amCharRefs.current[index] = chart;
      // Register with shared refs for cross-component syncing
      if (onChartRegistered) {
        onChartRegistered(index, chart);
      }
    }
  };

  const syncCursor = syncCursorUtil.bind(null, amCharRefs);

  // Create combined chart refs for syncing (includes both TimeseriesChart and external charts)
  const getCombinedChartRefs = (): (XYChart | null)[] => {
    const combinedRefs = [...amCharRefs.current];
    if (externalChartRefs?.current) {
      combinedRefs.push(...externalChartRefs.current);
    }
    return combinedRefs;
  };

  const syncAxes = (chart: any) => {
    const combinedRefs = { current: getCombinedChartRefs() };
    syncAxesUtil(combinedRefs, chart);
  };

  // Create charts when chart data changes
  useEffect(() => {
    if (!sortedData || sortedData.length === 0) return;

    // Clean up previous charts to prevent memory leaks
    chartRoots.forEach((root) => {
      if (root && typeof root.dispose === 'function') {
        try {
          root.dispose();
        } catch (e) {
          console.error('Error disposing chart:', e);
        }
      }
    });

    const newRoots: any = [];

    for (let i = 0; i < sortedData.length; i++) {
      const chartRef = chartRefs.current[i];
      const currentChart = sortedData[i];
      const currentTrendData = parameterData?.find((trend: any) => trend.name === currentChart.originalTitle);

      const chartConfigValues: ChartConfig = {
        chartColor: currentTrendData?.lineColor || '#00a86b',
        lineType: currentTrendData?.lineType || 'solid',
        lineWeight: currentTrendData?.lineWeight || 2,
        markerColor: currentTrendData?.markerColor || '#2A8C1D',
        markerType: currentTrendData?.markerType || 'circle',
        markerSize: currentTrendData?.markerSize || 4,
      };

      if (chartRef) {
        try {
          const chart = createTimeSeriesChart(
            chartRef,
            sortedData[i]?.dataPoints || [],
            chartConfigValues,
            sortedData[i]?.title,
            sortedData[i]?.shortUnitOfMeasure || '-',
            addAmChartRef,
            i,
            syncCursor,
            syncAxes,
            chartViewOptions,
            startDate,
            endDate,
          );
          newRoots.push(chart);
        } catch (e) {
          console.error(`Error creating chart ${i}:`, e);
          newRoots.push(null);
        }
      } else {
        newRoots.push(null);
      }
    }

    setChartRoots(newRoots);

    // Cleanup function
    return () => {
      newRoots.forEach((root: any) => {
        if (root && typeof root.dispose === 'function') {
          try {
            root.dispose();
          } catch (e) {
            console.error('Error disposing chart in cleanup:', e);
          }
        }
      });
    };
  }, [chartData, chartViewOptions, timeseriesChartData]);

  useEffect(() => {
    if (fullscreenChartIndex !== null && chartData[fullscreenChartIndex]) {
      const chart = sortedData[fullscreenChartIndex];
      const chartRef = document.getElementById('fullscreen-chart-container');
      if (chartRef) {
        const currentTrendData = parameterData?.find((trend: any) => trend.name === chart.originalTitle);

        const chartConfigValues: ChartConfig = {
          chartColor: currentTrendData?.lineColor || '#00a86b',
          lineType: currentTrendData?.lineType || 'solid',
          lineWeight: currentTrendData?.lineWeight || 2,
          markerColor: currentTrendData?.markerColor || '#2A8C1D',
          markerType: currentTrendData?.markerType || 'circle',
          markerSize: currentTrendData?.markerSize || 4,
        };
        try {
          createTimeSeriesChart(
            chartRef,
            chart?.dataPoints || [],
            chartConfigValues,
            chart?.title,
            chart?.shortUnitOfMeasure || '',
            addAmChartRef,
            fullscreenChartIndex,
            syncCursor,
            syncAxes,
            chartViewOptions,
            startDate,
            endDate,
          );
        } catch (e) {
          console.error('Error creating chart in fullscreen:', e);
        }
      }
    }
  }, [fullscreenChartIndex, chartData]);

  const handleFullscreen = (index: number) => {
    setFullscreenChartIndex(index);

    fullscreenTimeoutRef.current = setTimeout(() => {
      const fullscreenModal = document.querySelector('.fullscreen-modal');
      if (fullscreenModal && !document.fullscreenElement) {
        fullscreenModal.requestFullscreen().catch((err) => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
      }
    }, 0);
  };

  const handleCloseFullscreen = () => {
    setFullscreenChartIndex(null);

    if (fullscreenTimeoutRef.current) {
      clearTimeout(fullscreenTimeoutRef.current);
      fullscreenTimeoutRef.current = null;
    }

    // Exit fullscreen mode if active
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error(`Error attempting to exit fullscreen: ${err.message}`);
      });
    }
  };

  const handleChartDownload = (option: string, index: number) => {
    const chart = sortedData[index];
    const fileName = `${chart.title}${chart.shortUnitOfMeasure ? ` (${chart.shortUnitOfMeasure})` : ''}_COV-TimeSeriesChart`;
    const header = `${chart.title}${chart.shortUnitOfMeasure ? ` (${chart.shortUnitOfMeasure})` : ''}`;
    handleChartDownloadUtil(option, index, fullscreenChartIndex, fileName, header);
  };

  if (chartDataLoading) {
    return <Loader />;
  }

  if (!hasPlottableData) return <EmptyState />;

  return (
    <div className='stacked-chart-container'>
      <div className='flex flex-col gap-[8px] items-start self-stretch'>
        {sortedData?.map((chart: any, index: any) => (
          <section key={chart.id} className='chart-section'>
            {chart.dataPoints && chart.dataPoints.length > 0 ? (
              <div id={`chart-wrapper-${index}`} className='flex w-full flex-col justify-center items-center'>
                <div className='flex w-full flex-col justify-center items-end'>
                  <div className='w-full h-[181px] flex'>
                    <div ref={(el) => (chartRefs.current[index] = el)} className='chart-container' />
                    <div className='flex w-[175px] flex-col items-end'>
                      <div className='cov-trend-chart-controls'>
                        <ProtectAction canAccess={rPermission.canExpand}>
                          <button
                            analytics-id={`cov-trend-chart-fullscreen-btn-${index}`}
                            className='chart-action-button'
                            onClick={() => handleFullscreen(index)}
                          >
                            <FullScreenIcon />
                          </button>
                        </ProtectAction>
                        <DownloadChart
                          onClick={(option) => handleChartDownload(option, index)}
                          canExportJpg={rPermission.canExportJpg}
                          canExportPdf={rPermission.canExportPdf}
                        />
                      </div>
                      <div className='chart-tooltip'>
                        <span className='chart-tooltip__value'>
                          {chart.value}
                          {chart.shortUnitOfMeasure ? ` ${chart.shortUnitOfMeasure}` : ''}
                        </span>
                        <span className='chart-tooltip__time'>{chart.date}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className='chart-wrapper cov-no-data-wrapper'>
                  <NoDataComponent name={chart.title} />
                </div>
              </>
            )}
          </section>
        ))}
      </div>

      {/* Fullscreen Modal */}
      {fullscreenChartIndex !== null && sortedData[fullscreenChartIndex] && (
        <div className='fullscreen-modal'>
          <div className='fullscreen-header'>
            <div className='fullscreen-header-components'>
              <span className='fullcreen-header-content'>{sortedData[fullscreenChartIndex].title}</span>
              <button
                analytics-id='cov-fullscreen-modal-close-btn'
                className='close-button'
                onClick={handleCloseFullscreen}
              >
                <CloseIcon />
              </button>
            </div>
            <div className='min-max flex justify-between items-center gap-[15px]'>
              <DownloadChart
                onClick={(option) => handleChartDownload(option, fullscreenChartIndex)}
                canExportJpg={rPermission.canExportJpg}
                canExportPdf={rPermission.canExportPdf}
              />
            </div>
          </div>
          <div id='fullscreen-chart-container' className='fullscreen-chart-container' />
        </div>
      )}
    </div>
  );
};

export default COVTimeSeriesChart;
